import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

// Body scroll-lock shared by every modal. A reference count handles the (rare)
// case of stacked modals, so the lock is only released when the last one closes.
let openModalCount = 0;

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  // Extra class on the modal box, for per-dialog width/skin overrides.
  className?: string;
  // When false, skip the dimming/blur backdrop and sit below the nav so the
  // page behind stays visible and the nav stays clickable (section modals).
  dim?: boolean;
  // Show a ← back button left of the title instead of the ✕ (section modals).
  backButton?: boolean;
  // When false, a click on the backdrop does nothing (section modals: pages
  // that just present like modals shouldn't navigate away on a stray click).
  closeOnBackdrop?: boolean;
  // Hide the ✕ in the header (a dialog whose buttons already cover cancel).
  closeButton?: boolean;
}

// Lets a modal's content place things in the modal header: something at the
// right end of the title row (the shop's egg balance, the leaderboard's Top
// button) and a tab band under it (SectionTabs). Children call the hooks with
// a node; the modal renders the latest node it was given.
interface HeadSlots {
  setSlot: (node: React.ReactNode) => void;
  setTabs: (node: React.ReactNode) => void;
}
const ModalHeadContext = createContext<HeadSlots | null>(null);
export function useModalHeadSlot(node: React.ReactNode) {
  const ctx = useContext(ModalHeadContext);
  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.setSlot(node);
    return () => ctx.setSlot(null);
  }, [ctx, node]);
}
// Returns true when a modal took the node (the caller then renders nothing
// itself); false outside a modal, where the caller renders it inline.
export function useModalHeadTabs(node: React.ReactNode): boolean {
  const ctx = useContext(ModalHeadContext);
  useLayoutEffect(() => {
    if (!ctx) return;
    ctx.setTabs(node);
    return () => ctx.setTabs(null);
  }, [ctx, node]);
  return !!ctx;
}

// Generic neon modal used by Help / Login and other global dialogs.
export default function Modal({ title, onClose, children, footer, className, dim = true, backButton = false, closeOnBackdrop = true, closeButton = true }: ModalProps) {
  const [headSlot, setHeadSlot] = useState<React.ReactNode>(null);
  const [headTabs, setHeadTabs] = useState<React.ReactNode>(null);
  // One stable context value: a fresh object per render would re-render every
  // consumer, whose fresh nodes would set state here again, and so on.
  const slots = useMemo<HeadSlots>(() => ({ setSlot: setHeadSlot, setTabs: setHeadTabs }), []);
  // Escape closes any modal — quick, smooth dismissal back to what's behind.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock background scroll while a modal is open so the page behind can't scroll
  // under it. overflow: hidden (not a height cap) leaves the footer in place -
  // it's just unreachable until the modal closes, then scrolling is restored.
  useEffect(() => {
    openModalCount += 1;
    document.body.classList.add("app-modal-open");
    return () => {
      openModalCount -= 1;
      if (openModalCount <= 0) {
        openModalCount = 0;
        document.body.classList.remove("app-modal-open");
      }
    };
  }, []);

  // Publish the header's rendered height as --modal-head-h on the modal box.
  // On phones the header is sticky at the top of the page while the content
  // scrolls, and anything else that sticks (the leaderboard's column header)
  // parks right under it.
  const headRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const head = headRef.current;
    const box = head?.parentElement;
    if (!head || !box || typeof ResizeObserver === "undefined") return;
    const publish = () => box.style.setProperty("--modal-head-h", `${Math.ceil(head.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(head);
    return () => ro.disconnect();
  }, []);

  const box = (
    <div
      className={"app-modal-backdrop" + (dim ? "" : " app-modal-backdrop--clear")}
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div className={"app-modal" + (className ? " " + className : "")} onClick={(e) => e.stopPropagation()}>
        <div className={"app-modal-head" + (headTabs ? " app-modal-head--tabs" : "")} ref={headRef}>
          <div className="app-modal-head-row">
            {backButton && (
              <button className="app-modal-back" onClick={onClose} aria-label="Back" data-sound="back">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2>{title}</h2>
            {!backButton && closeButton && (
              <button className="app-icon-btn" onClick={onClose} aria-label="Close" data-sound="back">✕</button>
            )}
          </div>
          {/* Order on the page: title row and the slot on one line, the tab
              band on the line below. A phone on its side puts the band on the
              same line (see app-responsive.scss). */}
          {headTabs ? <div className="app-modal-head-tabs">{headTabs}</div> : null}
          {headSlot ? <div className="app-modal-head-slot">{headSlot}</div> : null}
        </div>
        <div className="app-modal-body">
          <ModalHeadContext.Provider value={slots}>{children}</ModalHeadContext.Provider>
        </div>
        {footer ? <div className="app-modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
  // A pop-up (dimmed) modal goes on top of the page, outside wherever it was opened
  // from: the game screen is its own stacking layer, and the between-rounds screens
  // above it (scores, the next round's card) covered its Settings. Section modals
  // (dim off) stay in the page, under the nav.
  return dim && typeof document !== "undefined" ? createPortal(box, document.body) : box;
}
